<script lang="ts">
  import {
    type Block,
    type ReviewState,
    type WithoutIdAndReview,
    canReview,
    canEdit,
  } from '@briefmarken_katalog/types';

  import DateOfIssue from './DateOfIssue.svelte';
  import Title from './Title.svelte';
  import FieldSeparator from './FieldSeparator.svelte';
  import Picture from '../Picture.svelte';
  import BlockEditor from '../editors/BlockEditor.svelte';
  import ReviewStateIcon from './ReviewStateIcon.svelte';
  import RowActionButtons from './RowActionButtons.svelte';
  import CatalogDetailDialog from './CatalogDetailDialog.svelte';

  import { user, doLogin } from '$lib/stores/auth';
  import { catalogMode } from '$lib/stores/ui';
  import { reviewSuggestion, getItem, getSuggestion, deleteSuggestion } from '$lib/services/philatelic_items';
  import { BlockEditorModel } from '../editors/models/BlockEditorModel.svelte';
  import { t } from '$lib/i18n';
  import { applyReviewResult } from './applyReviewResult';

  import './row.css';

  const { block, readonly = false }: {
    block: Block;
    readonly?: boolean;
  } = $props<{ block: Block; readonly?: boolean }>();

  let isEditorOpen = $state(false);
  let editorModel = $state<BlockEditorModel | null>(null);
  let catalogDetailItem = $state<Block | null>(null);
  let isCatalogDetailOpen = $state(false);

  async function fetchFull(): Promise<Block | null> {
    const fetch = block.review.state === 'Accepted' ? getItem : getSuggestion;
    return await fetch(block._id!, doLogin) as Block | null;
  }

  async function markAsInReview() {
    try {
      applyReviewResult(block, await reviewSuggestion(block._id!, 'InReview', undefined, doLogin));
    } catch (e: any) {
      alert($t('errors.markAsInReview') + e.message);
    }
  }

  async function markAsSuggested() {
    try {
      applyReviewResult(block, await reviewSuggestion(block._id!, 'Suggested', undefined, doLogin));
    } catch (e: any) {
      alert($t('errors.markAsInReview') + e.message);
    }
  }

  function suggestedBySuffix(full: Block): string {
    return full.review.suggestedByName
      ? ` ${$t('editor.suggestedBy')} ${full.review.suggestedByName}`
      : '';
  }

  async function openReview() {
    const full = await fetchFull();
    if (!full) return;
    editorModel = new BlockEditorModel({
      isReview: true,
      initialValue: full,
      dialogTitle: 'editor.blockReviewTitle',
      dialogSubtitle: 'editor.blockReviewSubtitle',
      onCreate: async (updatedBlock: WithoutIdAndReview<Block>) => {
        try {
          applyReviewResult(block, await reviewSuggestion(block._id!, block.review.state, updatedBlock, doLogin));
        } catch (e: any) {
          alert($t('errors.reviewFailed') + e.message);
        }
      },
      onReview: async (updatedBlock: WithoutIdAndReview<Block>, state: ReviewState) => {
        try {
          applyReviewResult(block, await reviewSuggestion(block._id!, state, updatedBlock, doLogin));
        } catch (e: any) {
          alert($t('errors.reviewFailed') + e.message);
        }
      },
    });
    editorModel.dialogSubtitleSuffix = suggestedBySuffix(full);
    isEditorOpen = true;
  }

  async function openDelete() {
    const full = await fetchFull();
    if (!full) return;
    editorModel = new BlockEditorModel({
      isDelete: true,
      initialValue: full,
      dialogTitle: 'deleteSuggestion.title',
      dialogSubtitle: 'deleteSuggestion.subtitle',
      onDelete: async () => {
        try {
          await deleteSuggestion(block._id!, doLogin);
        } catch (e: any) {
          alert($t('errors.deleteSuggestion') + e.message);
        }
      },
    });
    editorModel.dialogSubtitleSuffix = suggestedBySuffix(full);
    isEditorOpen = true;
  }

  async function openDetails(ro: boolean) {
    const full = await fetchFull();
    if (!full) return;
    // Catalog mode gets the presentation-style detail dialog instead of the editor
    if (ro && $catalogMode) {
      catalogDetailItem = full;
      isCatalogDetailOpen = true;
      return;
    }
    const subtitleKey = block.review.state !== 'Accepted'
      ? 'editor.blockDetailsSuggestionSubtitle' as const
      : 'editor.blockDetailsCatalogSubtitle' as const;
    editorModel = new BlockEditorModel({
      isReadonly: ro,
      isEdit: !ro,
      initialValue: full,
      dialogTitle: 'editor.blockDetailsTitle',
      dialogSubtitle: subtitleKey,
      ...(!ro && {
        onCreate: async (updatedBlock: WithoutIdAndReview<Block>) => {
          try {
            applyReviewResult(block, await reviewSuggestion(block._id!, block.review.state, updatedBlock, doLogin));
          } catch (e: any) {
            alert($t('errors.reviewFailed') + e.message);
          }
        },
      }),
    });
    if (block.review.state !== 'Accepted') editorModel.dialogSubtitleSuffix = suggestedBySuffix(full);
    isEditorOpen = true;
  }
</script>

<!-- Desktop: single flex line (wrappers dissolve via sm:contents / sm:order-*); mobile: stacked lines -->
<div class="row max-sm:flex-col max-sm:gap-1">
  <!-- mobile line 1: picture + title -->
  <div class="max-sm:flex max-sm:items-start max-sm:gap-1 max-sm:w-full sm:contents">
    <div class="shrink-0 sm:order-3">
      <Picture
        picture={block.pictures ? block.pictures[0] : undefined}
        fetchFullImage={() => fetchFull().then(f => f?.pictures?.[0])}
      />
    </div>
    <div class="min-w-0 flex-1 sm:order-5">
      <Title text={block.title}/>
    </div>
  </div>
  <!-- mobile line 2: date · review state · actions -->
  <div class="max-sm:flex max-sm:items-center max-sm:w-full sm:contents">
    <div class="sm:order-1">
      <DateOfIssue date={block.date_of_issue}/>
    </div>
    {#if !readonly}
      <div class="sm:order-6">
        <ReviewStateIcon
          state={block.review.state}
          reviewedBy={block.review.reviewedBy}
          currentUserId={$user?.id}
          userCanReview={!!$user && canReview($user.role)}
          onMarkAsInReview={markAsInReview}
          onDropReview={markAsSuggested}
        />
      </div>
      <div class="max-sm:ml-auto sm:order-8">
        <RowActionButtons
          state={block.review.state}
          reviewedBy={block.review.reviewedBy}
          currentUserId={$user?.id}
          userCanReview={!!$user && canReview($user.role)}
          userCanEdit={!!$user && canEdit(block, $user.id)}
          userIsAdmin={$user?.role === 'Admin'}
          userIsLoggedIn={!!$user}
          onReview={openReview}
          onDelete={openDelete}
          onDetails={openDetails}
        />
      </div>
    {:else}
      <div class="max-sm:ml-auto sm:order-8">
        <RowActionButtons
          readonly={true}
          state={block.review.state}
          reviewedBy={block.review.reviewedBy}
          currentUserId={$user?.id}
          userCanReview={!!$user && canReview($user.role)}
          userCanEdit={false}
          userIsAdmin={false}
          userIsLoggedIn={!!$user}
          onReview={openReview}
          onDetails={openDetails}
        />
      </div>
    {/if}
  </div>
  <!-- desktop-only vertical dividers -->
  <FieldSeparator class="sm:order-2"/>
  <FieldSeparator class="sm:order-4"/>
  {#if !readonly}
    <FieldSeparator class="sm:order-7"/>
  {/if}
</div>

{#if isEditorOpen && editorModel}
  <BlockEditor bind:model={editorModel!} bind:isOpen={isEditorOpen}/>
{/if}

{#if isCatalogDetailOpen && catalogDetailItem}
  <CatalogDetailDialog item={catalogDetailItem} bind:isOpen={isCatalogDetailOpen} />
{/if}
